/**
 * NotificationFrequencyManager
 * 
 * Controls notification throttling based on user's notificationFrequency setting (0-1).
 * Uses minimum interval between notifications for intuitive time-based control.
 * 
 * Current configuration (10 steps):
 * - frequency = 0:    Off - No notifications
 * - frequency = 0.1:  Every 60 minutes
 * - frequency = 0.2:  Every 50 minutes
 * - frequency = 0.3:  Every 40 minutes
 * - frequency = 0.4:  Every 30 minutes
 * - frequency = 0.5:  Every 20 minutes
 * - frequency = 0.6:  Every 15 minutes
 * - frequency = 0.7:  Every 10 minutes
 * - frequency = 0.8:  Every 5 minutes
 * - frequency = 0.9:  Every 2 minutes
 * - frequency = 1:    All notifications (unlimited)
 * 
 * To modify intervals, edit the getMinIntervalSeconds() method below.
 */
export class NotificationFrequencyManager {
    private lastNotificationTime: number = 0;
    private userFrequencySetting: number = 1;

    public setUserFrequency(frequency: number): void {
        this.userFrequencySetting = Math.max(0, Math.min(1, frequency));
    }

    public shouldAllowNotification(): boolean {
        if (this.userFrequencySetting === 1) {
            return true;
        }

        if (this.userFrequencySetting === 0) {
            return false;
        }

        const now = Date.now();
        const minInterval = this.getMinIntervalSeconds(this.userFrequencySetting) * 1000;
        const timeSinceLastNotification = now - this.lastNotificationTime;

        return timeSinceLastNotification >= minInterval;
    }

    public recordNotification(): void {
        this.lastNotificationTime = Date.now();
    }

    private getMinIntervalSeconds(frequency: number): number {
        if (frequency >= 1) return 0;
        if (frequency >= 0.9) return 120;
        if (frequency >= 0.8) return 300;
        if (frequency >= 0.7) return 600;
        if (frequency >= 0.6) return 900;
        if (frequency >= 0.5) return 1200;
        if (frequency >= 0.4) return 1800;
        if (frequency >= 0.3) return 2400;
        if (frequency >= 0.2) return 3000;
        if (frequency >= 0.1) return 3600;
        return Infinity;
    }
}

export const notificationFrequencyManager = new NotificationFrequencyManager();

